import { useEffect, useMemo, useState } from 'react';

import { useSession } from '../../app/useSession';
import { useToast } from '../../components/Toast';
import { supabase } from '../../lib/supabase';
import styles from './settings.module.css';

const NOTIFICATION_KINDS: { kind: string; label: string }[] = [
  { kind: 'lead_new', label: 'ליד חדש' },
  { kind: 'lead_assigned', label: 'ליד הוקצה' },
  { kind: 'quote_viewed', label: 'הצעת מחיר נצפתה' },
  { kind: 'quote_accepted', label: 'הצעת מחיר אושרה' },
  { kind: 'deal_stuck', label: 'דיל תקוע' },
  { kind: 'deal_won', label: 'דיל נסגר בזכייה' },
  { kind: 'invoice_paid', label: 'חשבונית שולמה' },
  { kind: 'invoice_overdue', label: 'חשבונית באיחור' },
  { kind: 'task_due', label: 'משימה בעוד 24 שעות' },
  { kind: 'mention', label: 'תויגת בתגובה' },
  { kind: 'comment', label: 'תגובה חדשה' },
  { kind: 'meeting_soon', label: 'פגישה בעוד 30 דקות' },
  { kind: 'whatsapp_inbound', label: 'הודעת WhatsApp חדשה' },
  { kind: 'project_completed', label: 'פרויקט הושלם' },
  { kind: 'system_update', label: 'עדכון מערכת' },
  { kind: 'error', label: 'שגיאה' },
];

interface Pref {
  kind: string;
  in_app: boolean;
  email: boolean;
  whatsapp: boolean;
}

export function NotificationsPrefsPage() {
  const session = useSession();
  const userId = session.status === 'signed-in' ? session.session.user.id : null;
  const toast = useToast();

  const [prefsByKind, setPrefsByKind] = useState<Record<string, Pref>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    const cancel = { current: false };
    void (async () => {
      const { data } = await supabase
        .from('notification_preferences')
        .select('kind, in_app, email, whatsapp')
        .eq('user_id', userId);
      if (cancel.current) return;
      const byKind: Record<string, Pref> = {};
      (data ?? []).forEach((p) => { byKind[p.kind] = p; });
      NOTIFICATION_KINDS.forEach(({ kind }) => {
        byKind[kind] ??= { kind, in_app: true, email: false, whatsapp: false };
      });
      setPrefsByKind(byKind);
      setLoading(false);
    })();
    return () => { cancel.current = true; };
  }, [userId]);

  async function toggle(kind: string, channel: 'in_app' | 'email' | 'whatsapp') {
    if (!userId) return;
    const current = prefsByKind[kind] ?? { kind, in_app: true, email: false, whatsapp: false };
    const next = { ...current, [channel]: !current[channel] };
    setPrefsByKind((cur) => ({ ...cur, [kind]: next }));
    setSaving(`${kind}-${channel}`);
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert(
          { user_id: userId, kind, in_app: next.in_app, email: next.email, whatsapp: next.whatsapp },
          { onConflict: 'user_id,kind' },
        );
      if (error) throw error;
    } catch {
      // Revert on error
      setPrefsByKind((cur) => ({ ...cur, [kind]: current }));
      toast.error('לא הצלחנו לשמור · נסה שוב');
    } finally {
      setSaving(null);
    }
  }

  const rows = useMemo(
    () => NOTIFICATION_KINDS.map(({ kind, label }) => ({
      kind,
      label,
      pref: prefsByKind[kind] ?? { kind, in_app: true, email: false, whatsapp: false },
    })),
    [prefsByKind],
  );

  return (
    <>
      <div className={styles.pageHead}>
        <h1>העדפות התראות</h1>
        <p className={styles.pageSub}>בחר אילו סוגי התראות מגיעות אליך ובאיזה ערוץ. תוך-אפליקציה (Bell) פעיל כברירת מחדל; אימייל ו-WhatsApp יחוברו ב-Phase B.</p>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3>מטריצה · {String(rows.length)} סוגי התראות</h3>
        </div>

        {loading ? <p className={styles.notice}>טוען...</p> : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>התראה</th>
                <th style={{ textAlign: 'center', width: 110 }}>תוך-האפליקציה</th>
                <th style={{ textAlign: 'center', width: 110 }}>אימייל</th>
                <th style={{ textAlign: 'center', width: 110 }}>WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ kind, label, pref }) => (
                <tr key={kind}>
                  <td>{label} <code dir="ltr" style={{ marginInlineStart: 6, fontSize: 10 }}>{kind}</code></td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={pref.in_app}
                      onChange={() => { void toggle(kind, 'in_app'); }}
                      disabled={saving === `${kind}-in_app`}
                      aria-label={`${label} · תוך-האפליקציה`}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={pref.email}
                      onChange={() => { void toggle(kind, 'email'); }}
                      disabled
                      title="ערוץ אימייל יחובר ב-Phase B (Resend)"
                      aria-label={`${label} · אימייל`}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={pref.whatsapp}
                      onChange={() => { void toggle(kind, 'whatsapp'); }}
                      disabled
                      title="ערוץ WhatsApp יחובר ב-Phase B (Bot Cloud API)"
                      aria-label={`${label} · WhatsApp`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
